import type { SubmitEventHandler } from 'react';
import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type {
  Media,
  Variant,
} from '../../lib/flotiq-client/types/api-response';
import type {
  LockFormFn,
  ReloadContentObjectFn,
} from '../../lib/flotiq-plugins/plugin-events/MediaFormAddElementEvent';

import { use, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import * as Y from 'yjs';
import z from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import {
  getDefaultImageProperties,
  getTestProps,
  isImagePreviewSupported,
} from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import UserContext from '../../contexts/UserContext';
import { WebsocketContext } from '../../contexts/WebsocketContext';

import useYjsFormObserver from '../../form-hooks/collaboration/useYjsFormObserver';
import { useAppForm } from '../../form-hooks/form';

type MediaFormValues = {
  fileName?: string;
  alt?: string;
  title?: string;
  variants?: Variant[];
};

export type MediaFormSubmitFn = (
  values: MediaFormValues,
) => Promise<
  [
    [MediaFormValues | Media | undefined, Record<string, string> | undefined],
    boolean,
  ]
>;

interface MediaFormProps {
  /**
   * On submit handler
   */
  onSubmit: MediaFormSubmitFn;
  /**
   * User data
   */
  media: Media | null;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * Names of the fields disabled on top of permissions and `disabled`
   */
  lockedFields?: string[];
  /**
   * If submitting the form is blocked
   */
  submitDisabled?: boolean;
  /**
   * Locks fields and saving of the form, passed to plugins
   */
  lockForm?: LockFormFn;
  /**
   * Fetches the media again, passed to plugins
   */
  reloadContentObject?: ReloadContentObjectFn;
  /**
   * Callback for updating form state
   */
  setFormState?: SetFormApiFn;
  /**
   * Form id
   */
  formId?: string;
  /**
   * If variants should be shown in the form
   */
  withoutVariants?: boolean;
  /**
   * User form test id
   */
  testId?: string;
}

export const getInitialMediaValues = (
  media?: Media | MediaFormValues | null,
  yjsDocument?: Y.Doc,
) => {
  const doc = yjsDocument || new Y.Doc();
  const yMap = doc.getMap('vals');

  const fileNameGroup = (media?.fileName || '').match(
    /(?<fileName>.*)\.(?<extenstion>[^.]+)$/,
  )?.groups;

  if (!yMap.size) {
    doc.transact(() => {
      yMap.set(
        'fileName',
        new Y.Text(fileNameGroup?.fileName || media?.fileName || ''),
      );
      yMap.set('fileExtension', new Y.Text(fileNameGroup?.extenstion || ''));
      yMap.set('title', new Y.Text(media?.title || ''));
      yMap.set('alt', new Y.Text(media?.alt || ''));

      const variantsArray = new Y.Array<Variant>();
      yMap.set('variants', variantsArray);
      (media as Media)?.variants?.forEach((variant) =>
        variantsArray.push([variant]),
      );
    });
  }

  return yMap.toJSON();
};

const MediaForm = ({
  media,
  onSubmit,
  disabled = false,
  lockedFields,
  submitDisabled = false,
  lockForm,
  reloadContentObject,
  setFormState,
  formId = 'media-form',
  withoutVariants = false,
  testId = '',
}: MediaFormProps) => {
  const { yjsDocument, isYjsDirty } = use(WebsocketContext);
  const { t } = useTranslation();
  const noPreview =
    media?.type !== 'image' || !isImagePreviewSupported(media?.extension);

  const { permissions } = use(UserContext);

  const validationSchema = useMemo(
    () =>
      z.object({
        fileName: z.string().min(1, t('Form.FormErrorNotBlank')),
        fileExtension: z.string(),
        alt: z.string(),
        title: z.string(),
        variants: z.array(
          z.object({
            name: z.string().min(1, t('Form.FormErrorNotBlank')),
            trim: z.object({
              width: z.number().int().min(1, t('Form.FormErrorNotBlank')),
              height: z.number().int().min(1, t('Form.FormErrorNotBlank')),
              top: z.number().int().min(0, t('Form.FormErrorNotBlank')),
              left: z.number().int().min(0, t('Form.FormErrorNotBlank')),
            }),
          }),
        ),
      }),
    [t],
  );

  const preview = useMemo(() => {
    if (noPreview) {
      return (
        <div className="flex items-center justify-center w-fit order-first mt-4">
          <div className="flex items-center justify-center rounded-full h-12 w-12 bg-blue-300">
            {media?.extension || ''}
          </div>
        </div>
      );
    }
    return (
      <img
        src={getMediaUrl(media)}
        alt={media.fileName}
        className={'max-h-80 object-center object-contain'}
        {...getDefaultImageProperties()}
        {...getTestProps(testId, 'image')}
      />
    );
  }, [media, noPreview, testId]);

  const isFieldDisabled = useCallback(
    (fieldName: string) =>
      !!lockedFields?.includes(fieldName) ||
      !permissions.canCo(
        '_media',
        RolePermissions.PERMISSIONS_TYPES.UPDATE,
        fieldName,
      ),
    [permissions, lockedFields],
  );

  const isFieldVisible = useCallback(
    (fieldName: string) =>
      permissions.canCo(
        '_media',
        RolePermissions.PERMISSIONS_TYPES.READ,
        fieldName,
      ),
    [permissions],
  );

  const form = useAppForm({
    defaultValues: getInitialMediaValues(media, yjsDocument),
    onSubmit: async ({ value, formApi }) => {
      const newValues = {
        fileName: value.fileExtension
          ? value.fileName + '.' + value.fileExtension
          : value.fileName,
        ...(value.alt ? { alt: value.alt } : {}),
        ...(value.title ? { title: value.title } : {}),
        ...(value.variants ? { variants: value.variants } : {}),
      };

      for (const property in newValues) {
        if (
          !permissions.canCo(
            '_media',
            RolePermissions.PERMISSIONS_TYPES.UPDATE,
            property,
          )
        ) {
          delete newValues[property as keyof typeof newValues];
        }
      }

      const [[newMedia, errors], hasErrors] = await onSubmit(newValues);

      if (!hasErrors) {
        formApi.reset(getInitialMediaValues(newMedia, yjsDocument));
      } else {
        setFormSubmitErrors(formApi, errors);
      }
    },
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (submitDisabled) return;
      form.handleSubmit();
    },
    [form, submitDisabled],
  );

  useYjsFormObserver(form, yjsDocument);

  return (
    <form.AppForm>
      <form
        id={formId}
        className="flex flex-col gap-4 md:gap-6"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <div
          className={twMerge(
            'w-full flex flex-col gap-4 md:gap-6',
            !withoutVariants &&
              'p-7 md:p-6 bg-white dark:bg-slate-950 rounded-lg ',
          )}
        >
          <div className="max-w-(--breakpoint-md) space-y-4">
            <form.CustomFormElement />
            <div
              className="flex w-full gap-2"
              {...getTestProps(testId, 'inputs')}
            >
              {noPreview && preview}

              {isFieldVisible('fileName') && (
                <form.AppField name="fileName">
                  {(field) => (
                    <field.Input
                      label={t('Media.Name')}
                      disabled={isFieldDisabled('fileName') || disabled}
                      required
                      {...getTestProps(testId, 'fileName', 'testId')}
                    />
                  )}
                </form.AppField>
              )}

              <form.Subscribe selector={({ values }) => values.fileExtension}>
                {(fileExtension) =>
                  fileExtension && isFieldVisible('extension') ? (
                    <form.AppField name="fileExtension">
                      {(field) => (
                        <field.Input
                          label={t('Media.Extension')}
                          disabled
                          additionalClasses="w-max"
                          {...getTestProps(testId, 'extension', 'testId')}
                        />
                      )}
                    </form.AppField>
                  ) : (
                    ''
                  )
                }
              </form.Subscribe>
            </div>
            {media && lockForm && reloadContentObject && (
              <form.CustomMediaFormElement
                media={media}
                disabled={disabled}
                lockForm={lockForm}
                reloadContentObject={reloadContentObject}
              />
            )}
            {isFieldVisible('title') && (
              <form.AppField name="title">
                {(field) => (
                  <field.Input
                    label={t('Media.Title')}
                    disabled={isFieldDisabled('title') || disabled}
                    additionalClasses={noPreview ? ' row-span-1' : ''}
                    {...getTestProps(testId, 'title', 'testId')}
                  />
                )}
              </form.AppField>
            )}
            {isFieldVisible('alt') && (
              <form.AppField name="alt">
                {(field) => (
                  <field.Input
                    label={t('Media.Alt')}
                    disabled={isFieldDisabled('alt') || disabled}
                    additionalClasses={noPreview ? ' row-span-2' : ''}
                    {...getTestProps(testId, 'alt', 'testId')}
                  />
                )}
              </form.AppField>
            )}
            {!noPreview && preview}
          </div>
        </div>
        {!withoutVariants && !noPreview && isFieldVisible('variants') && (
          <>
            <form.Subscribe selector={({ values }) => values.variants}>
              {(variants) => (
                <p className="text-lg font-bold dark:text-slate-200">
                  {t('MediaEdit.ExistingVariants', {
                    length: variants?.length || 0,
                  })}
                </p>
              )}
            </form.Subscribe>

            <div className="bg-white dark:bg-slate-950 w-full rounded-lg gap-4 md:gap-6 p-7 md:p-6">
              <form.AppField name="variants" mode="array">
                {(field) => (
                  <field.VariantsField
                    media={media}
                    disabled={isFieldDisabled('variants') || disabled}
                    {...getTestProps(testId, 'variants', 'testId')}
                  />
                )}
              </form.AppField>
            </div>
          </>
        )}
        <form.DirtyHandler dirty={isYjsDirty} />
        <form.FormStateHandler setFormState={setFormState} />
      </form>
    </form.AppForm>
  );
};

export default MediaForm;
